import axios, { type AxiosError, type AxiosResponse } from 'axios';

interface ApiErrorResponseBody {
  statusCode?: number;
  code?: string;
  message?: string | string[];
  error?: string;
}

interface ApiErrorMetadata {
  statusCode?: number;
  code?: string;
  details?: unknown;
  cause?: unknown;
}

export interface ParsedApiError {
  statusCode: number;
  code: string;
  message: string;
  error: string;
}

const FALLBACK_MESSAGE = '请求失败，请稍后重试';

export class ApiRequestError extends Error {
  statusCode: number;
  code: string;
  details?: unknown;

  constructor(message: string, { statusCode = 500, code = 'INTERNAL_SERVER_ERROR', details, cause }: ApiErrorMetadata = {}) {
    super(message);
    this.name = new.target.name;
    this.statusCode = statusCode;
    this.code = code;
    this.details = details;

    if (cause !== undefined) {
      (this as Error & { cause?: unknown }).cause = cause;
    }
  }
}

export class ApiNotFoundError extends ApiRequestError {
  constructor(message = 'This item was not found or may have been removed.', metadata: ApiErrorMetadata = {}) {
    super(message, {
      statusCode: metadata.statusCode ?? 404,
      code: metadata.code ?? 'RESOURCE_NOT_FOUND',
      details: metadata.details,
      cause: metadata.cause,
    });
  }
}

export class ApiAuthError extends ApiRequestError {
  constructor(message = 'You are not authorized to perform this action.', metadata: ApiErrorMetadata = {}) {
    super(message, {
      statusCode: metadata.statusCode ?? 401,
      code: metadata.code ?? 'AUTH_UNAUTHORIZED',
      details: metadata.details,
      cause: metadata.cause,
    });
  }
}

export class ApiServerError extends ApiRequestError {
  constructor(message = 'The backend service is temporarily unavailable.', metadata: ApiErrorMetadata = {}) {
    super(message, {
      statusCode: metadata.statusCode ?? 503,
      code: metadata.code ?? 'SERVICE_UNAVAILABLE',
      details: metadata.details,
      cause: metadata.cause,
    });
  }
}

export class ApiNetworkError extends ApiRequestError {
  constructor(message = 'The backend service is temporarily unavailable.', metadata: ApiErrorMetadata = {}) {
    super(message, {
      statusCode: metadata.statusCode ?? 503,
      code: metadata.code ?? 'NETWORK_ERROR',
      details: metadata.details,
      cause: metadata.cause,
    });
  }
}

export class ApiUnknownError extends ApiRequestError {
  constructor(message = FALLBACK_MESSAGE, metadata: ApiErrorMetadata = {}) {
    super(message, {
      statusCode: metadata.statusCode ?? 500,
      code: metadata.code ?? 'UNKNOWN_ERROR',
      details: metadata.details,
      cause: metadata.cause,
    });
  }
}

export function isApiRequestError(error: unknown): error is ApiRequestError {
  return error instanceof ApiRequestError;
}

export function classifyApiError(error: unknown): ApiRequestError {
  if (error instanceof ApiRequestError) {
    return error;
  }

  if (axios.isAxiosError(error)) {
    return classifyAxiosError(error);
  }

  if (error instanceof Error) {
    return new ApiUnknownError(error.message || FALLBACK_MESSAGE, {
      cause: error,
    });
  }

  return new ApiUnknownError(FALLBACK_MESSAGE, {
    details: error,
  });
}

export function parseApiError(error: unknown): ParsedApiError {
  const classified = classifyApiError(error);

  return {
    statusCode: classified.statusCode,
    code: classified.code,
    message: classified.message || FALLBACK_MESSAGE,
    error: mapErrorName(classified),
  };
}

export function getUserFriendlyMessage(error: unknown, fallback = FALLBACK_MESSAGE) {
  const parsed = parseApiError(error);
  return parsed.message || fallback;
}

function classifyAxiosError(error: AxiosError): ApiRequestError {
  const data = toApiErrorResponseBody(error.response?.data);
  const statusCode = data.statusCode ?? error.response?.status;
  const message = toMessage(data.message) || defaultMessageForStatus(statusCode);
  const code = data.code ?? defaultCodeForStatus(statusCode);
  const details = {
    url: error.config?.url,
    method: error.config?.method,
    axiosCode: error.code,
    response: error.response?.data,
  };

  if (!error.response || isNetworkFailureCode(error.code)) {
    return new ApiNetworkError(message, {
      statusCode: statusCode ?? 503,
      code: code === 'UNKNOWN_ERROR' ? 'NETWORK_ERROR' : code,
      details,
      cause: error,
    });
  }

  if (statusCode === 401 || statusCode === 403) {
    return new ApiAuthError(message, {
      statusCode,
      code,
      details,
      cause: error,
    });
  }

  if (statusCode === 404) {
    if (isBackendJsonResponse(error.response, data)) {
      return new ApiNotFoundError(message, {
        statusCode,
        code,
        details,
        cause: error,
      });
    }

    // A non-JSON 404 usually means request misrouting (e.g. frontend host fallback).
    return new ApiNetworkError('The backend service is temporarily unavailable.', {
      statusCode: 503,
      code: 'BACKEND_UNAVAILABLE',
      details,
      cause: error,
    });
  }

  if (typeof statusCode === 'number' && statusCode >= 500) {
    return new ApiServerError(message, {
      statusCode,
      code,
      details,
      cause: error,
    });
  }

  return new ApiUnknownError(message || FALLBACK_MESSAGE, {
    statusCode: statusCode ?? 500,
    code,
    details,
    cause: error,
  });
}

function toApiErrorResponseBody(value: unknown): ApiErrorResponseBody {
  if (!isObject(value)) {
    return {};
  }

  const body: ApiErrorResponseBody = {};

  if (typeof value.statusCode === 'number') {
    body.statusCode = value.statusCode;
  }

  if (typeof value.code === 'string') {
    body.code = value.code;
  }

  if (typeof value.error === 'string') {
    body.error = value.error;
  }

  if (typeof value.message === 'string' || Array.isArray(value.message)) {
    body.message = value.message;
  }

  return body;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toMessage(message: string | string[] | undefined) {
  if (typeof message === 'string') {
    return message;
  }

  if (Array.isArray(message)) {
    return message.filter((item): item is string => typeof item === 'string').join('，');
  }

  return '';
}

function defaultCodeForStatus(statusCode: number | undefined) {
  switch (statusCode) {
    case 401:
      return 'AUTH_UNAUTHORIZED';
    case 403:
      return 'AUTH_FORBIDDEN';
    case 404:
      return 'RESOURCE_NOT_FOUND';
    case 409:
      return 'RESOURCE_CONFLICT';
    case 400:
      return 'BAD_REQUEST';
    case 500:
    case 502:
    case 503:
      return 'SERVICE_UNAVAILABLE';
    default:
      return 'UNKNOWN_ERROR';
  }
}

function defaultMessageForStatus(statusCode: number | undefined) {
  switch (statusCode) {
    case 401:
      return '登录状态无效或已过期，请重新登录';
    case 403:
      return '无权限访问该资源';
    case 404:
      return 'This item was not found or may have been removed.';
    case 400:
      return '请求参数不合法';
    case 500:
    case 502:
    case 503:
      return 'The backend service is temporarily unavailable.';
    default:
      return FALLBACK_MESSAGE;
  }
}

function mapErrorName(error: ApiRequestError) {
  if (error instanceof ApiAuthError) {
    return 'Unauthorized';
  }

  if (error instanceof ApiNotFoundError) {
    return 'Not Found';
  }

  if (error instanceof ApiServerError || error instanceof ApiNetworkError) {
    return 'Service Unavailable';
  }

  return 'Internal Server Error';
}

function isNetworkFailureCode(code: string | undefined) {
  if (!code) {
    return false;
  }

  return (
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    code === 'ENOTFOUND' ||
    code === 'ECONNREFUSED'
  );
}

function isBackendJsonResponse(response: AxiosResponse | undefined, body: ApiErrorResponseBody) {
  if (!response) {
    return false;
  }

  const contentType = getHeaderValue(response.headers, 'content-type');

  if (typeof contentType === 'string' && contentType.toLowerCase().includes('application/json')) {
    return true;
  }

  return Boolean(body.statusCode || body.code || body.error || body.message);
}

function getHeaderValue(headers: AxiosResponse['headers'], key: string): string | undefined {
  if (!headers) {
    return undefined;
  }

  if (typeof (headers as Record<string, unknown>).get === 'function') {
    const value = (headers as { get: (header: string) => string | null }).get(key);
    return value ?? undefined;
  }

  const direct = (headers as Record<string, unknown>)[key];
  if (typeof direct === 'string') {
    return direct;
  }

  const lowered = (headers as Record<string, unknown>)[key.toLowerCase()];
  if (typeof lowered === 'string') {
    return lowered;
  }

  return undefined;
}
